import React, { useEffect, useState } from "react";
import { fetchPlans, createPlan, deletePlan, fetchPayments, fetchPlanLogs } from "../api/client";
import { Sparkles, Trash2, ShieldAlert, Plus, Check, Edit2, RefreshCw } from "lucide-react";
import "./Plans.css";

const EMPTY_PLAN = {
  name: "",
  price: "",
  yearly_price: "",
  allowed_licenses: 1,
  mt5_per_license_allowed: 1,
  num_strategies_allowed: 0,
  support: "chatbot"
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};


export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_PLAN);
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // Audit states
  const [payments, setPayments] = useState([]);
  const [planLogs, setPlanLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const isSupportChecked = (val) => {
    if (!form.support) return false;
    return form.support.toLowerCase().includes(val.toLowerCase());
  };

  const handleSupportToggle = (val) => {
    let selected = form.support ? form.support.split(",").map(s => s.trim().toLowerCase()) : [];
    if (selected.includes(val.toLowerCase())) {
      selected = selected.filter(s => s !== val.toLowerCase());
    } else {
      selected.push(val.toLowerCase());
    }
    setForm(p => ({ ...p, support: selected.join(", ") }));
  };

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await fetchPlans();
      setPlans(data);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to load plans", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadAuditData = async () => {
    setAuditLoading(true);
    try {
      const [payData, logData] = await Promise.all([
        fetchPayments(),
        fetchPlanLogs()
      ]);
      setPayments(payData);
      setPlanLogs(logData);
    } catch (err) {
      console.error("Failed to load audit data", err);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
    loadAuditData();
  }, []);

  const handleEdit = (plan) => {
    setForm({
      name: plan.name,
      price: plan.price.toString(),
      yearly_price: plan.yearly_price ? plan.yearly_price.toString() : "",
      allowed_licenses: plan.allowed_licenses,
      mt5_per_license_allowed: plan.mt5_per_license_allowed,
      num_strategies_allowed: plan.num_strategies_allowed,
      support: plan.support
    });
    setEditingId(plan.id);
    setShowForm(true);
  };

  const handleCreate = async () => {
    if (!form.name.trim() || form.price === "") {
      showToast("Plan name and price are required", "error");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        yearly_price: form.yearly_price !== "" ? Number(form.yearly_price) : null,
        allowed_licenses: Number(form.allowed_licenses),
        mt5_per_license_allowed: Number(form.mt5_per_license_allowed),
        num_strategies_allowed: Number(form.num_strategies_allowed)
      };
      await createPlan(payload);
      showToast(editingId ? "Plan updated successfully!" : "Plan created successfully!");
      setForm(EMPTY_PLAN);
      setEditingId(null);
      setShowForm(false);
      loadPlans();
      loadAuditData();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to save plan", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (planId, planName) => {
    if (planName.toLowerCase() === "demo") {
      showToast("Default Demo plan cannot be deleted.", "error");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete the plan "${planName}"?`)) return;
    setLoading(true);
    try {
      await deletePlan(planId);
      showToast("Plan deleted successfully");
      loadPlans();
      loadAuditData();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to delete plan", "error");
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_PLAN);
  };

  return (
    <div className="plans-container" style={{ animation: "fadeIn 0.3s ease" }}>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

      <div className="page-header">
        <div>
          <div className="page-title">Manage Subscription Plans</div>
          <div className="page-sub">Configure dynamic licenses, connections, and pricing tiers for users</div>
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => { setShowForm(true); setEditingId(null); setForm(EMPTY_PLAN); }}
          style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px" }}
        >
          <Plus size={16} /> Add Plan
        </button>
      </div>

      {/* Plans List Cards Grid */}
      {loading && plans.length === 0 ? (
        <div className="loading" style={{ padding: "60px", textAlign: "center" }}>Loading active plans...</div>
      ) : plans.length === 0 ? (
        <div className="card empty" style={{ padding: "60px" }}>
          No plans configured. Click "+ Add Plan" to initialize package tiers.
        </div>
      ) : (
        <div className="plans-grid">
          {plans.map(p => {
            const isDemo = p.name.toLowerCase() === "demo";
            return (
              <div key={p.id} className={`plan-card ${isDemo ? "current-active" : ""}`} style={{ minHeight: "360px" }}>
                {isDemo && (
                  <span 
                    className="current-badge" 
                    style={{ 
                      background: "rgba(16, 185, 129, 0.1)", 
                      borderColor: "rgba(16, 185, 129, 0.2)", 
                      color: "var(--green)" 
                    }}
                  >
                    Default Demo
                  </span>
                )}
                
                <div className="plan-header">
                  <div className="plan-tier-name" style={{ color: "var(--accent)", display: "flex", alignItems: "center", gap: "8px" }}>
                    {p.name}
                  </div>
                  <div className="plan-price-box">
                    {p.price === 0 ? (
                      <span className="plan-price-value" style={{ fontSize: "32px" }}>Free</span>
                    ) : (
                      <>
                        <span className="plan-currency-symbol">₹</span>
                        <span className="plan-price-value">{p.price}</span>
                        <span className="plan-price-period">/ month</span>
                      </>
                    )}
                  </div>
                  {p.price > 0 && (
                    <div style={{ fontSize: "12px", color: "var(--green)", marginTop: "4px", fontWeight: "600" }}>
                      ₹{p.yearly_price ?? Math.round(p.price * 12 * 0.8)} / year <span style={{ fontSize: "10px", opacity: 0.7 }}>(Save 20%)</span>
                    </div>
                  )}
                </div>

                <ul className="plan-feature-list" style={{ marginBottom: "24px" }}>
                  <li className="plan-feature-row">
                    <Check size={14} className="plan-feature-icon" />
                    <span><strong>{p.allowed_licenses}</strong> License {p.allowed_licenses === 1 ? "ID" : "IDs"}</span>
                  </li>
                  <li className="plan-feature-row">
                    <Check size={14} className="plan-feature-icon" />
                    <span><strong>{p.mt5_per_license_allowed}</strong> MT5 connections / ID</span>
                  </li>
                  <li className="plan-feature-row">
                    <Check size={14} className="plan-feature-icon" />
                    <span><strong>{p.num_strategies_allowed}</strong> TV strategies built</span>
                  </li>
                  <li className="plan-feature-row">
                    <Check size={14} className="plan-feature-icon" />
                    <span style={{ textTransform: "capitalize" }}><strong>{p.support}</strong> support</span>
                  </li>
                </ul>

                <div 
                  style={{ 
                    display: "flex", 
                    gap: "10px", 
                    marginTop: "auto", 
                    borderTop: "1px solid var(--border)", 
                    paddingTop: "16px" 
                  }}
                >
                  <button
                    className="btn btn-ghost"
                    style={{ flex: 1, justifyContent: "center", padding: "8px", fontSize: "13px" }}
                    onClick={() => handleEdit(p)}
                    disabled={loading}
                  >
                    Edit Plan
                  </button>
                  {!isDemo && (
                    <button
                      className="btn btn-danger"
                      style={{ flex: 1, justifyContent: "center", padding: "8px", fontSize: "13px" }}
                      onClick={() => handleDelete(p.id, p.name)}
                      disabled={loading}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 💳 User Payments Audit Trail Section */}
      <div className="card" style={{ marginTop: "40px", padding: "28px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h3 style={{ fontSize: "18px", fontWeight: "800", color: "var(--text)" }}>User Payments History</h3>
            <p style={{ fontSize: "12px", color: "var(--text2)", marginTop: "4px" }}>Audit trail of all package purchases made by premium subscribers</p>
          </div>
          <button 
            className="btn btn-ghost" 
            onClick={loadAuditData} 
            disabled={auditLoading} 
            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px" }}
          >
            <RefreshCw size={14} className={auditLoading ? "spin" : ""} />
            {auditLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        
        {payments.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "var(--text2)", border: "1px dashed var(--border)", borderRadius: "12px" }}>
            No payments recorded yet. Transactions will appear here on user checkout.
          </div>
        ) : (
          <div className="table-responsive" style={{ overflowX: "auto" }}>
            <table className="admin-plans-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Plan</th>
                  <th>Billing</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Payment ID</th>
                  <th>PAN / Tax ID</th>
                  <th>Date &amp; Time</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight: "600", color: "var(--text)" }}>{p.user_name}</div>
                      <div style={{ fontSize: "11px", color: "var(--text2)" }}>{p.user_email}</div>
                    </td>
                    <td>
                      <span className="current-badge" style={{ position: "static", background: "rgba(0, 242, 254, 0.08)", color: "var(--accent)", border: "1px solid rgba(0, 242, 254, 0.15)", padding: "4px 10px" }}>
                        {p.plan_name}
                      </span>
                    </td>
                    <td style={{ textTransform: "capitalize", fontSize: "12px", color: "var(--text2)" }}>{p.billing_period || "monthly"}</td>
                    <td style={{ fontWeight: "700", color: "var(--green)" }}>&#8377;{p.amount.toFixed(2)}</td>
                    <td style={{ textTransform: "uppercase", fontSize: "12px", fontWeight: "600", color: "var(--text2)" }}>{p.payment_method}</td>
                    <td style={{ fontFamily: "monospace", fontSize: "11px", color: "var(--text2)" }}>{p.razorpay_payment_id || "—"}</td>
                    <td style={{ fontFamily: "monospace", color: "var(--text)", fontSize: "13px" }}>{p.pan_tax_id || "N/A"}</td>
                    <td style={{ fontSize: "12.5px", color: "var(--text2)" }}>{formatDate(p.purchased_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 📜 Plan Configuration Changes History Log */}
      <div className="card" style={{ marginTop: "40px", padding: "28px", marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div>
            <h3 style={{ fontSize: "18px", fontWeight: "800", color: "var(--text)" }}>Plan Change Logs</h3>
            <p style={{ fontSize: "12px", color: "var(--text2)", marginTop: "4px" }}>Timeline of subscription package additions, updates, and deletions</p>
          </div>
        </div>

        {planLogs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "var(--text2)", border: "1px dashed var(--border)", borderRadius: "12px" }}>
            No configuration events logged yet.
          </div>
        ) : (
          <div style={{ 
            display: "flex", 
            flexDirection: "column", 
            gap: "14px", 
            background: "rgba(0,0,0,0.15)", 
            borderRadius: "12px", 
            padding: "20px", 
            border: "1px solid var(--border)", 
            maxHeight: "350px", 
            overflowY: "auto", 
            fontFamily: "monospace" 
          }}>
            {planLogs.map(log => {
              let actionColor = "var(--accent)";
              let actionLabel = "UPDATE";
              if (log.action === "admin_plan_created") {
                actionColor = "var(--green)";
                actionLabel = "CREATE";
              } else if (log.action === "admin_plan_deleted") {
                actionColor = "var(--red)";
                actionLabel = "DELETE";
              }
              return (
                <div key={log.id} style={{ display: "flex", flexWrap: "wrap", gap: "12px", borderBottom: "1px solid rgba(255,255,255,0.03)", paddingBottom: "12px" }}>
                  <div style={{ color: "var(--text2)", minWidth: "150px", fontSize: "12px" }}>
                    [{formatDate(log.timestamp)}]
                  </div>
                  <div style={{ minWidth: "70px" }}>
                    <span style={{
                      background: `rgba(${actionLabel === "CREATE" ? "16,185,129" : actionLabel === "DELETE" ? "239,68,68" : "0,242,254"}, 0.1)`,
                      color: actionColor,
                      border: `1px solid rgba(${actionLabel === "CREATE" ? "16,185,129" : actionLabel === "DELETE" ? "239,68,68" : "0,242,254"}, 0.15)`,
                      padding: "2px 6px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      fontWeight: "700"
                    }}>
                      {actionLabel}
                    </span>
                  </div>
                  <div style={{ flex: 1, minWidth: "250px", fontSize: "12.5px", color: "var(--text)" }}>
                    <div style={{ marginBottom: "4px" }}>{log.details}</div>
                    <div style={{ fontSize: "11px", color: "var(--text2)" }}>
                      Executed by: {log.user_name} ({log.user_email})
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 🚀 Add/Edit Plan Modal Dialog */}
      {showForm && (
        <div className="checkout-modal-overlay">
          <div className="checkout-modal" style={{ maxWidth: "600px" }}>
            <div className="checkout-header">
              <h4 className="checkout-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Sparkles size={18} className="accent" />
                {editingId ? `Modify Plan: ${form.name}` : "Create New Subscription Plan"}
              </h4>
              <button className="close-modal-btn" onClick={closeForm}>✕</button>
            </div>
            
            <div className="checkout-content" style={{ padding: "24px 28px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: "11px" }}>Plan Name</label>
                    <input
                      className="form-input"
                      placeholder="e.g. Starter Multiplexer"
                      value={form.name}
                      onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      disabled={loading || form.name.toLowerCase() === "demo"}
                    />
                    {form.name.toLowerCase() === "demo" && (
                      <small style={{ color: "var(--text2)", fontSize: "11px", display: "block", marginTop: "4px" }}>
                        Default trial package name cannot be renamed.
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: "11px" }}>Monthly Price (₹ / month)</label>
                    <input
                      className="form-input"
                      type="number"
                      placeholder="0 for Free"
                      value={form.price}
                      onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontSize: "11px" }}>Yearly Price (₹ / year) — leave blank to auto-calculate (monthly × 12 × 0.8)</label>
                  <input
                    className="form-input"
                    type="number"
                    placeholder={form.price ? `Auto: ₹${Math.round(Number(form.price) * 12 * 0.8)}` : "Auto-calculated"}
                    value={form.yearly_price}
                    onChange={e => setForm(p => ({ ...p, yearly_price: e.target.value }))}
                    disabled={loading}
                  />
                </div>

                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: "11px" }}>Allowed License IDs</label>
                    <input
                      className="form-input"
                      type="number"
                      placeholder="1"
                      value={form.allowed_licenses}
                      onChange={e => setForm(p => ({ ...p, allowed_licenses: e.target.value }))}
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: "11px" }}>MT5 Connections per License</label>
                    <input
                      className="form-input"
                      type="number"
                      placeholder="1"
                      value={form.mt5_per_license_allowed}
                      onChange={e => setForm(p => ({ ...p, mt5_per_license_allowed: e.target.value }))}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: "11px" }}>TradingView Strategies Built</label>
                    <input
                      className="form-input"
                      type="number"
                      placeholder="0"
                      value={form.num_strategies_allowed}
                      onChange={e => setForm(p => ({ ...p, num_strategies_allowed: e.target.value }))}
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: "span 2" }}>
                    <label className="form-label" style={{ fontSize: "11px", marginBottom: "8px", display: "block" }}>Support Operations (Multiple Select Checklist)</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "8px", padding: "12px 16px" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--text)" }}>
                        <input
                          type="checkbox"
                          checked={isSupportChecked("chatbot")}
                          onChange={() => handleSupportToggle("chatbot")}
                          style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "var(--accent)" }}
                        />
                        Chatbot
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--text)" }}>
                        <input
                          type="checkbox"
                          checked={isSupportChecked("email")}
                          onChange={() => handleSupportToggle("email")}
                          style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "var(--accent)" }}
                        />
                        Email Support
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--text)" }}>
                        <input
                          type="checkbox"
                          checked={isSupportChecked("telegram")}
                          onChange={() => handleSupportToggle("telegram")}
                          style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "var(--accent)" }}
                        />
                        Telegram Channel
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--text)" }}>
                        <input
                          type="checkbox"
                          checked={isSupportChecked("contact number")}
                          onChange={() => handleSupportToggle("contact number")}
                          style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "var(--accent)" }}
                        />
                        Personal Contact Number
                      </label>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div className="checkout-footer" style={{ justifyContent: "flex-end", gap: "12px" }}>
              <button className="btn btn-ghost" onClick={closeForm} disabled={loading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={loading}>
                <Check size={16} /> {editingId ? "Update Plan" : "Add Plan"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
